import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import AppSidebar from './AppSidebar';
import ContentHeader from './ContentHeader';
import { findNavItemByPath } from './navigation-config';

/**
 * AppShell - The single canonical layout for all dashboard pages
 * 
 * Implements the official ShadCN dashboard shell pattern:
 * - SidebarProvider as root (provides flex min-h-svh w-full)
 * - AppSidebar as left sibling (uses fixed positioning with spacer div)
 * - SidebarInset as right sibling containing header + main content
 * 
 * The sidebar reserves space via a spacer div in the flex layout.
 * The actual sidebar content is position:fixed but the spacer ensures
 * the main content doesn't overlap.
 * 
 * Header alignment: Both sidebar header and content header have h-12
 * with border-b, creating a unified horizontal divider line.
 */
const AppShell = () => {
  const location = useLocation();
  
  // Derive page title from navigation config
  const currentNavItem = findNavItemByPath(location.pathname);
  const pageTitle = currentNavItem?.label || 'Dashboard';

  return (
    <SidebarProvider>
      {/* Left: Sidebar - reserves space via spacer div, actual content is fixed */}
      <AppSidebar />
      
      {/* Right: Main content area - SidebarInset is already <main> with flex-1 */}
      <SidebarInset className="flex flex-col">
        {/* Header row - same height as sidebar header (h-12) for aligned divider */}
        <ContentHeader pageTitle={pageTitle} />
        
        {/* Page content - flex-1 to fill remaining space, overflow-auto for scrolling */}
        <div className="flex-1 overflow-auto p-4 md:p-6">
          <Outlet />
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
};

export default AppShell;
